import React, { useEffect, useState } from 'react';
import { useDashboard } from '../../../../context/dashboardContext';
import { FileText, Search, X } from 'lucide-react';

// Lists every PDF in the Cosen library (babs-drive-sync) and lets the user pick one.
// Picking calls onSelect({ key, name }) — the parent then loads its bytes into the editor.
const CosenLibraryModal = ({ isOpen, onClose, onSelect }) => {
  const { getCosenLibrary } = useDashboard();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setQuery('');
    (async () => {
      const res = await getCosenLibrary();
      if (cancelled) return;
      if (res.success) setItems(res.items);
      else setError(res.error || 'Failed to load library');
      setLoading(false);
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  if (!isOpen) return null;

  const q = query.trim().toLowerCase();
  const filtered = q ? items.filter((it) => it.name.toLowerCase().includes(q)) : items;
  const fmtSize = (b) => (b ? `${(b / 1048576).toFixed(1)} MB` : '');

  return (
    <div className="fixed inset-0 bg-gray-900 bg-opacity-60 flex items-center justify-center z-[55] p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl h-[80vh] flex flex-col">
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <h3 className="text-base font-semibold text-gray-800">Select from Cosen Library</h3>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="h-5 w-5" /></button>
        </div>

        <div className="p-4 border-b">
          <div className="relative">
            <Search className="h-4 w-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by model name…"
              className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-red-400"
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="p-8 text-center text-sm text-gray-500">Loading library…</div>
          ) : error ? (
            <div className="p-8 text-center text-sm text-red-500">{error}</div>
          ) : filtered.length === 0 ? (
            <div className="p-8 text-center text-sm text-gray-500">No PDFs match “{query}”.</div>
          ) : (
            <ul className="divide-y divide-gray-100">
              {filtered.map((it) => (
                <li key={it.key}>
                  <button
                    type="button"
                    onClick={() => onSelect(it)}
                    className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-red-50 transition-colors"
                  >
                    <FileText className="h-5 w-5 text-red-500 flex-shrink-0" />
                    <span className="flex-1 text-sm text-gray-800 truncate">{it.name}</span>
                    <span className="text-xs text-gray-400 flex-shrink-0">{fmtSize(it.size)}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="px-4 py-2 border-t text-xs text-gray-500">
          {loading ? '' : `${filtered.length} of ${items.length} PDFs`}
        </div>
      </div>
    </div>
  );
};

export default CosenLibraryModal;
